"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

type Props = {
  transparentHeader: boolean;
  children: React.ReactNode;
};

/**
 * The theme's outer .wrapper. It also takes over two jobs the theme did on
 * <body>, since <body> lives in the root layout and cannot vary by page:
 *
 *  - The transparent-header mode, for pages whose first section is a full-bleed
 *    hero the header sits over. The wrapper is the `site` group and carries
 *    data-transparent-header, plus data-scrolled once the page has left the
 *    hero (body-scrolled.js), so the header, page nav and main area style
 *    themselves with group-data-[transparent-header=true]/site: and
 *    group-data-[scrolled=false]/site:.
 *  - The scroll reveal for .js-das-reveal elements (das-reveal.js). It uses an
 *    observer rather than a scroll listener so anything already in view on
 *    load shows straight away.
 */
export default function SiteWrapper({ transparentHeader, children }: Props) {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const items = document.querySelectorAll<HTMLElement>(
      ".js-das-reveal:not(.is-revealed)",
    );

    if (!("IntersectionObserver" in window)) {
      items.forEach((item) => item.classList.add("is-revealed"));

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const item = entry.target as HTMLElement;
          const delay = parseFloat(item.dataset.revealDelay ?? "") || 0;

          if (delay) {
            item.style.transitionDelay = `${delay}s`;
          }

          item.classList.add("is-revealed");
          observer.unobserve(item);
        });
      },
      { threshold: 0.2 },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [pathname]);

  return (
    <div
      className="group/site relative flex flex-row flex-wrap overflow-hidden"
      data-transparent-header={transparentHeader}
      data-scrolled={scrolled}
    >
      <div className="flex min-h-screen w-full flex-col">{children}</div>
    </div>
  );
}
